import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Form, InputGroup, Button, Spinner } from 'react-bootstrap';
import { Styles } from '../styles/blogSidebar.js';
import { BLOGS_ENDPOINT } from '../../../config/api';

const BlogSidebar = () => {
    const history = useHistory();
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const initialSearch = params.get('search') || '';

    const [searchValue, setSearchValue] = useState(initialSearch);
    const [recentPosts, setRecentPosts] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const controller = new AbortController();

        const loadSidebarData = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await fetch(`${BLOGS_ENDPOINT}?limit=50`, { signal: controller.signal });
                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload.message || 'Unable to load blog highlights.');
                }
                const payload = await response.json();
                const blogs = Array.isArray(payload.blogs) ? payload.blogs : [];

                const sorted = blogs
                    .slice()
                    .sort((a, b) => {
                        const aTime = a?.publishedAt ? new Date(a.publishedAt).getTime() : 0;
                        const bTime = b?.publishedAt ? new Date(b.publishedAt).getTime() : 0;
                        return bTime - aTime;
                    });

                setRecentPosts(sorted.slice(0, 5));

                const tagSet = new Set();
                sorted.forEach((blog) => {
                    if (Array.isArray(blog.tags)) {
                        blog.tags.forEach((tag) => {
                            if (typeof tag === 'string' && tag.trim()) {
                                tagSet.add(tag.trim());
                            }
                        });
                    }
                });
                setTags(Array.from(tagSet).slice(0, 12));
            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err.message || 'Unable to load blog highlights.');
                    setRecentPosts([]);
                    setTags([]);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadSidebarData();

        return () => controller.abort();
    }, []);

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        const trimmed = searchValue.trim();
        const query = new URLSearchParams();
        if (trimmed) {
            query.set('search', trimmed);
        }
        const base = `${process.env.PUBLIC_URL || ''}/blog-grid`;
        history.push(`${base}${query.toString() ? `?${query.toString()}` : ''}`);
    };

    const handleTagClick = (tag) => {
        setSearchValue(tag);
        const query = new URLSearchParams();
        query.set('search', tag);
        const base = `${process.env.PUBLIC_URL || ''}/blog-grid`;
        history.push(`${base}?${query.toString()}`);
    };

    return (
        <Styles>
            <div className="blog-sidebar">
                <div className="widget search-widget">
                    <h5>Search</h5>
                    <Form onSubmit={handleSearchSubmit}>
                        <InputGroup>
                            <Form.Control
                                type="text"
                                placeholder="Search blogs..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                            <InputGroup.Append>
                                <Button type="submit" variant="success">Go</Button>
                            </InputGroup.Append>
                        </InputGroup>
                    </Form>
                </div>

                <div className="widget recent-posts-widget">
                    <div className="widget-header">
                        <h5>Recent Posts</h5>
                    </div>
                    {loading ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" role="status" size="sm" />
                        </div>
                    ) : error ? (
                        <p className="muted">{error}</p>
                    ) : recentPosts.length === 0 ? (
                        <p className="muted">No recent posts available.</p>
                    ) : (
                        <ul className="list-unstyled">
                            {recentPosts.map((post) => {
                                const publishedDate = post.publishedAt
                                    ? new Date(post.publishedAt).toLocaleDateString()
                                    : '';
                                const slugPath = `/blog-details${post.slug ? `?slug=${encodeURIComponent(post.slug)}` : ''}`;
                                return (
                                    <li key={post._id}>
                                        <Link to={process.env.PUBLIC_URL + slugPath}>
                                            <span className="title">{post.title}</span>
                                            {publishedDate && <span className="date">{publishedDate}</span>}
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="widget tag-widget">
                    <div className="widget-header">
                        <h5>Tags</h5>
                    </div>
                    {loading ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" role="status" size="sm" />
                        </div>
                    ) : tags.length === 0 ? (
                        <p className="muted">No tags available yet.</p>
                    ) : (
                        <div className="tag-list">
                            {tags.map((tag) => (
                                <button
                                    key={tag}
                                    type="button"
                                    onClick={() => handleTagClick(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Styles>
    );
};

export default BlogSidebar;
